import AgoraDatePicker from '@components/V3/Utils/InputsV3/AgoraDatePicker';
import Select from '@components/V4/Select/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import SessionModalFormWrapper from '@modules/Admin/components/DetailsModal/DetailsModalFormWrapper';
import { useCreateEnrollment } from '@actions';
import { GetAllCoursesLiteDTO } from '@shared/clientTypes/DTOs/CoursesDTO';
import useToast from 'apps/agora/src/hooks/useToast';
import { BasicProps } from 'apps/agora/src/utils/types';
import moment from 'moment';
import { useContext } from 'react';
import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MentorDetailsPageContext } from '../../../MentorDetailsPage';
import TextArea from '@components/V4/Inputs/Textarea';

interface EnrollmentFormProps extends BasicProps {
  courses?: GetAllCoursesLiteDTO[];
  onCloseEditForm: () => void;
}

const enrollmentSchema = z.object({
  course: z.string().min(1, { message: 'Please select a course' }),
  startDate: z.string({ message: 'Please select a start date' }),
  endDate: z.string({ message: 'Please select an end date' }),
  comment: z.string().optional(),
});

type FormData = z.infer<typeof enrollmentSchema>;

const EnrolledInEnrollmentForm = (props: EnrollmentFormProps) => {
  const { courses, onCloseEditForm } = props;

  const { mentor } = useContext(MentorDetailsPageContext);

  const mentorId = mentor?._id ?? '';

  const toast = useToast();

  const { mutate: createEnrollment, isLoading: creatingEnrollment } = useCreateEnrollment({
    onSuccess: () => {
      toast.success('Enrollment added successfully.');
      onCloseEditForm();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message);
    },
  });

  const { register, handleSubmit, trigger, setValue, control, getValues } = useForm<FormData>({
    resolver: zodResolver(enrollmentSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  const { field: courseField, fieldState: courseFieldState } = useController({
    name: 'course',
    control,
  });

  const { field: startDateField, fieldState: startDateFieldState } = useController({
    name: 'startDate',
    control,
  });

  const { field: endDateField, fieldState: endDateFieldState } = useController({
    name: 'endDate',
    control,
  });

  const touchAllFields = (
    fields: Partial<Record<keyof FormData, string | number | { _id: string; fullName: string }>>
  ) => {
    Object.keys(fields).forEach((key) => {
      setValue(key as keyof FormData, getValues()[key as keyof FormData], {
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger();

    if (isValid && mentorId) {
      createEnrollment({
        userId: mentorId,
        courseId: data.course,
        subject: 'Undecided',
        mentorId: undefined,
        startDate: data.startDate,
        estimatedEndDate: data.endDate,
        numberOfSessions: 0,
        initialComment: data.comment,
      });
    }
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    const formData = getValues();

    touchAllFields(formData);

    handleSubmit(onSubmit)(e);
  };

  return (
    <SessionModalFormWrapper
      title={'New Enrollment'}
      submitButtonText="Create Enrollment"
      isLoading={creatingEnrollment}
      onCancel={onCloseEditForm}
      submitHandler={submitHandler}
    >
      <Select
        size="large"
        isRequired
        label="Course"
        options={
          courses?.map((course) => ({
            label: course.title,
            value: course._id,
          })) ?? []
        }
        value={courseField.value}
        onSelect={(value) => courseField.onChange(value)}
        onClear={() => courseField.onChange(undefined)}
        onBlur={courseField.onBlur}
        isValid={!courseFieldState.error}
        isTouched={courseFieldState.isTouched}
        errorText={courseFieldState.error?.message}
      />
      <div className="flex justify-between gap-4">
        <AgoraDatePicker
          isRequired
          label="Start Date"
          allowClear={false}
          value={startDateField.value ? moment(startDateField.value) : undefined}
          onChange={(value) => startDateField.onChange(moment(value).format('YYYY-MM-DD'))}
          onBlur={startDateField.onBlur}
          isValid={!startDateFieldState.error}
          isTouched={startDateFieldState.isTouched}
          errorText={startDateFieldState.error?.message}
        />
        <AgoraDatePicker
          isRequired
          label="End Date"
          allowClear={false}
          value={endDateField.value ? moment(endDateField.value) : undefined}
          onChange={(value) => endDateField.onChange(moment(value).format('YYYY-MM-DD'))}
          onBlur={endDateField.onBlur}
          isValid={!endDateFieldState.error}
          isTouched={endDateFieldState.isTouched}
          errorText={endDateFieldState.error?.message}
        />
      </div>
      <div className="flex justify-between gap-4">
        <TextArea id="comment" label="Comment" className="h-40" {...register('comment')} />
      </div>
    </SessionModalFormWrapper>
  );
};

export default EnrolledInEnrollmentForm;
